import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import Pii from 'dashboard/components/Pii';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { VerificationDigitalIdUkSharecode } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import { paragraphMixins } from 'design-system/mixins/text';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const InfoSection = styled('div')`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

const ImageGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${Spacing[4]};
  padding-top: ${Spacing[2]};
`;

const PhotoLabel = styled.p`
  ${paragraphMixins.lgBold};
  margin: ${Spacing[2]} 0;
  text-align: center;
`;

interface DigitalIdUkSharecodeVerificationProps extends VerificationDetailProps {
  verification: VerificationDigitalIdUkSharecode;
}

const DigitalIdUkSharecodeVerification = ({
  verification,
}: DigitalIdUkSharecodeVerificationProps) => {
  const {
    status,
    sharecode,
    birthdate,
    'redacted-at': redactedAt,
    'name-full': nameFull,
    'reference-number': referenceNumber,
    'right-to-work-details': rightToWorkDetails,
    'center-photo-url': centerPhotoUrl,
  } = verification.attributes;

  const redacted = redactedAt != null;
  const showChecks =
    status != null &&
    ![
      VerificationStatus.Initiated,
      VerificationStatus.Confirmed,
      VerificationStatus.Submitted,
      VerificationStatus.Canceled,
    ].includes(status);

  const checks = verification.attributes.checks;

  return (
    <InfoSection>
      {centerPhotoUrl && (
        <Pii key='portrait-photo' isRedacted={redacted} hideOnRedacted={true}>
          <ImageGridContainer>
            <div key='portrait-photo-with-label'>
              <AsyncImage
                imageUrl={centerPhotoUrl}
                width='initial'
                height='250px'
                modalLabel='Portrait photo'
                allowModalExpand={true}
              />
              <PhotoLabel>Portrait photo</PhotoLabel>
            </div>
          </ImageGridContainer>
        </Pii>
      )}

      <StyledAttributeGrid>
        <Attribute label='Sharecode' value={sharecode} redacted={redacted} />
        <Attribute label='Reference number' value={referenceNumber} redacted={redacted} />
        <Attribute label='Full name' value={nameFull} redacted={redacted} />
        <Attribute label='Birthdate' value={formatDate(birthdate)} redacted={redacted} />
        <Attribute label='Right to work details' value={rightToWorkDetails} redacted={redacted} />
      </StyledAttributeGrid>
      {showChecks && checks && <ChecksTable checks={checks} />}
    </InfoSection>
  );
};

export default DigitalIdUkSharecodeVerification;
